import React from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SeoHead from "../components/SeoHead";

const sections = [
  {
    title: "1. Agreement to Terms",
    content: (
      <>
        <p>
          These Terms of Service (&quot;Terms&quot;) govern your access to and use of Ghost Inbox
          (&quot;Ghost Inbox,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), including
          the website{" "}
          <a
            href="https://ghostinbox.xyz"
            className="text-emerald-400 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://ghostinbox.xyz
          </a>{" "}
          and all related features (collectively, the &quot;Service&quot;).
        </p>
        <p className="mt-3">
          Ghost Inbox provides temporary email inbox services for software testing, quality
          assurance, privacy protection, and legitimate business purposes. By using Ghost Inbox,
          you agree to comply with these Terms of Service.
        </p>
        <p className="mt-3">
          By creating an account, accessing, or using the Service, you agree to be bound by these
          Terms, our{" "}
          <Link to="/privacy" className="text-emerald-400 hover:underline">
            Privacy Policy
          </Link>
          , and our{" "}
          <Link to="/acceptable-use" className="text-emerald-400 hover:underline">
            Acceptable Use Policy
          </Link>
          . If you do not agree, you must not use the Service.
        </p>
      </>
    ),
  },
  {
    title: "2. Description of Service",
    content: (
      <>
        <p>
          Ghost Inbox provides secure, account-linked temporary email inboxes using the
          @ghostinbox.xyz domain. Registered users can choose a username, receive inbound email in
          a web dashboard, and view messages in near real time with automatic refresh.
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>
            The Service is intended for software testing, quality assurance, email workflow
            validation, account verification testing, and privacy protection.
          </li>
          <li>Messages and inboxes expire automatically after approximately 20 minutes.</li>
          <li>
            Ghost Inbox is not a replacement for a permanent personal or business email provider.
          </li>
          <li>
            We do not guarantee uninterrupted availability, message delivery, or inbox permanence.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "3. Eligibility",
    content: (
      <p>
        You must be at least 13 years of age (or the minimum legal age in your jurisdiction) and
        capable of forming a binding contract to use the Service. By using Ghost Inbox, you
        represent and warrant that you meet these requirements and that all registration
        information you provide is accurate and current.
      </p>
    ),
  },
  {
    title: "4. Account Registration and Security",
    content: (
      <>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            You must provide a valid name, email address, and password to create an account.
          </li>
          <li>
            You are solely responsible for maintaining the confidentiality of your login
            credentials and for all activity under your account.
          </li>
          <li>
            You must notify us promptly if you suspect unauthorized access to your account.
          </li>
          <li>
            You may not share your account with others or use another person&apos;s account without
            permission.
          </li>
          <li>
            We reserve the right to refuse registration, suspend, block, or terminate accounts at
            our discretion, including for violations of these Terms or suspected abuse.
          </li>
        </ul>
        <p className="mt-3">
          If your account is blocked, you may contact us through our{" "}
          <Link to="/contact" className="text-emerald-400 hover:underline">
            Contact Us
          </Link>{" "}
          page to request review. Deleted accounts may register again using the same email address,
          subject to these Terms.
        </p>
      </>
    ),
  },
  {
    title: "5. Acceptable Use Policy",
    content: (
      <>
        <p className="font-medium text-zinc-300">Permitted uses</p>
        <p className="mt-2">Users may use Ghost Inbox for:</p>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          <li>Software development</li>
          <li>Quality assurance testing</li>
          <li>User acceptance testing</li>
          <li>Email workflow testing</li>
          <li>Spam protection</li>
          <li>Privacy protection</li>
          <li>Educational purposes</li>
        </ul>
        <p className="mt-4 font-medium text-zinc-300">Prohibited activities</p>
        <p className="mt-2">Users may not use Ghost Inbox for:</p>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Spam:</strong> Sending or supporting unsolicited
            communications.
          </li>
          <li>
            <strong className="text-zinc-300">Phishing:</strong> Collecting credentials, personal
            information, or financial information.
          </li>
          <li>
            <strong className="text-zinc-300">Fraud:</strong> Creating deceptive accounts or
            participating in fraudulent activities.
          </li>
          <li>
            <strong className="text-zinc-300">Malware:</strong> Distributing malicious software.
          </li>
          <li>
            <strong className="text-zinc-300">Illegal activities:</strong> Any activity prohibited
            by applicable laws.
          </li>
          <li>
            <strong className="text-zinc-300">Platform abuse:</strong> Attempting to overload
            systems, bypass rate limits, scrape infrastructure, or attack services.
          </li>
          <li>
            <strong className="text-zinc-300">Impersonation:</strong> Misrepresenting identity or
            affiliation.
          </li>
          <li>
            Using temporary inboxes for banking, healthcare, legal, government, or other regulated
            communications that require permanent records.
          </li>
          <li>
            Reselling, sublicensing, or commercially exploiting the Service without our written
            consent.
          </li>
        </ul>
        <p className="mt-3">
          See our full{" "}
          <Link to="/acceptable-use" className="text-emerald-400 hover:underline">
            Acceptable Use Policy
          </Link>{" "}
          for additional detail. Violation of this section may result in immediate suspension or
          termination of your account and, where appropriate, referral to law enforcement.
        </p>
      </>
    ),
  },
  {
    title: "6. Email Retention and Service Limitations",
    content: (
      <>
        <p className="font-medium text-zinc-300">Email retention</p>
        <p className="mt-2">
          Ghost Inbox inboxes are temporary. Emails may be automatically deleted after a predefined
          retention period (approximately 20 minutes). Users acknowledge that email storage is not
          guaranteed. Ghost Inbox should not be used for critical communications.
        </p>
        <p className="mt-4 font-medium text-zinc-300">Additional limitations</p>
        <ul className="mt-2 list-disc space-y-2 pl-5">
          <li>
            <strong className="text-zinc-300">Account-linked access:</strong> Inboxes are tied to
            registered accounts. Do not treat temporary inboxes as secure vaults for sensitive or
            regulated information.
          </li>
          <li>
            <strong className="text-zinc-300">No SLA:</strong> We do not warrant specific uptime,
            delivery speed, or compatibility with third-party websites that block temporary inbox
            domains.
          </li>
          <li>
            <strong className="text-zinc-300">Third-party services:</strong> Some websites may
            reject @ghostinbox.xyz addresses. We are not responsible for third-party acceptance
            policies.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "7. Account Responsibility",
    content: (
      <p>
        Users are solely responsible for how they use Ghost Inbox. Ghost Inbox is not responsible
        for user-generated content or third-party communications received through temporary
        inboxes. You retain responsibility for any content sent to or from your temporary inbox.
      </p>
    ),
  },
  {
    title: "8. Service Availability",
    content: (
      <>
        <p>
          Ghost Inbox provides services on a best-effort basis. We do not guarantee continuous
          uptime, permanent email storage, or delivery of all messages. Services may be modified,
          suspended, or discontinued without notice.
        </p>
        <p className="mt-3">
          The Service is provided on an &quot;as is&quot; and &quot;as available&quot; basis without
          warranties of any kind, whether express, implied, or statutory, including implied
          warranties of merchantability, fitness for a particular purpose, title, and
          non-infringement.
        </p>
      </>
    ),
  },
  {
    title: "9. Abuse Monitoring",
    content: (
      <p>
        To maintain platform integrity, Ghost Inbox reserves the right to monitor abuse indicators,
        block suspicious traffic, restrict abusive users, and remove content violating these Terms.
        We may review content when investigating abuse, security incidents, or legal compliance
        obligations.
      </p>
    ),
  },
  {
    title: "10. Intellectual Property",
    content: (
      <>
        <p>
          The Service, including its name, logo, design, software, text, and graphics, is owned
          by Ghost Inbox or its licensors and is protected by intellectual property laws. We grant
          you a limited, non-exclusive, non-transferable, revocable license to access and use the
          Service in accordance with these Terms.
        </p>
        <p className="mt-3">
          You may not copy, modify, distribute, reverse engineer, or create derivative works of
          any part of the Service without our prior written permission.
        </p>
      </>
    ),
  },
  {
    title: "11. Limitation of Liability",
    content: (
      <>
        <p className="uppercase text-zinc-300">
          To the maximum extent permitted by applicable law, Ghost Inbox and its operators,
          affiliates, and suppliers shall not be liable for data loss, lost communications, service
          interruptions, business losses, indirect damages, or any indirect, incidental, special,
          consequential, or punitive damages arising from or related to your use of the Service.
        </p>
        <p className="mt-3">
          Users assume all risks associated with using temporary inbox services. Our total aggregate
          liability for any claim arising out of or relating to the Service shall not exceed the
          greater of (a) the amount you paid us, if any, in the twelve (12) months preceding the
          claim, or (b) one hundred U.S. dollars (USD $100). Some jurisdictions do not allow
          certain limitations, so some of the above may not apply to you.
        </p>
      </>
    ),
  },
  {
    title: "12. Termination",
    content: (
      <>
        <p>
          Ghost Inbox may suspend or terminate access for users who violate these Terms of
          Service, with or without notice, if we reasonably believe you pose a security risk or if
          continued access could harm the Service or other users.
        </p>
        <p className="mt-3">
          You may stop using the Service at any time and may request account deletion by contacting
          us. Upon termination, your right to use the Service ceases immediately.
        </p>
      </>
    ),
  },
  {
    title: "13. Indemnification",
    content: (
      <p>
        You agree to defend, indemnify, and hold harmless Ghost Inbox and its operators from and
        against any claims, liabilities, damages, losses, and expenses (including reasonable legal
        fees) arising out of or related to your use of the Service, your violation of these Terms,
        or your violation of any third-party rights or applicable law.
      </p>
    ),
  },
  {
    title: "14. Governing Law and Disputes",
    content: (
      <p>
        These Terms shall be governed by and construed in accordance with applicable laws, without
        regard to conflict-of-law principles. Any dispute arising from these Terms or the Service
        shall first be attempted to be resolved informally by contacting us. If informal resolution
        is unsuccessful, disputes shall be subject to the exclusive jurisdiction of the courts
        located in the jurisdiction where Ghost Inbox operates, unless mandatory consumer
        protection laws in your country require otherwise.
      </p>
    ),
  },
  {
    title: "15. Changes to These Terms",
    content: (
      <p>
        We may modify these Terms at any time. When we do, we will update the &quot;Last
        updated&quot; date above. Material changes may be communicated through the Service or by
        other reasonable means. Your continued use of Ghost Inbox after updated Terms take effect
        constitutes acceptance of the revised Terms.
      </p>
    ),
  },
  {
    title: "16. Severability and Entire Agreement",
    content: (
      <p>
        If any provision of these Terms is found to be invalid or unenforceable, the remaining
        provisions will remain in full force and effect. These Terms, together with our Privacy
        Policy and Acceptable Use Policy, constitute the entire agreement between you and Ghost
        Inbox regarding the Service and supersede prior agreements on the same subject.
      </p>
    ),
  },
  {
    title: "17. Contact Information",
    content: (
      <p>
        For questions about these Terms, account status, or the Service, please visit our{" "}
        <Link to="/contact" className="text-emerald-400 hover:underline">
          Contact Us
        </Link>{" "}
        page at{" "}
        <a
          href="https://ghostinbox.xyz/contact"
          className="text-emerald-400 hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          ghostinbox.xyz/contact
        </a>
        .
      </p>
    ),
  },
];

function Terms() {
  return (
    <Layout>
      <SeoHead
        title="Terms of Service – Ghost Inbox"
        description="Ghost Inbox Terms of Service. Understand acceptable use, prohibited activities, email retention, service availability, and legal terms for our privacy-focused temporary inbox platform."
        path="/terms"
      />

      <div className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400">Legal</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Terms of Service</h1>
        <p className="mt-4 text-sm text-zinc-500">Last updated: 10 June 2026</p>
        <p className="mt-4 leading-relaxed text-zinc-400">
          Please read these Terms carefully before using Ghost Inbox. They explain the rules,
          responsibilities, and limitations that apply to your use of our temporary inbox platform.
        </p>

        <div className="mt-12 space-y-6">
          {sections.map((section) => (
            <section
              key={section.title}
              className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6"
            >
              <h2 className="text-lg font-semibold text-white">{section.title}</h2>
              <div className="mt-3 space-y-3 text-sm leading-relaxed text-zinc-400">
                {section.content}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 flex flex-col gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-400">
            Also read our{" "}
            <Link to="/privacy" className="text-emerald-400 hover:underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/acceptable-use" className="text-emerald-400 hover:underline">
              Acceptable Use Policy
            </Link>
            .
          </p>
          <Link
            to="/contact"
            className="inline-block rounded-lg border border-zinc-700 px-5 py-2.5 text-center text-sm text-zinc-300 hover:border-zinc-500"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </Layout>
  );
}

export default Terms;
